import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Download,
  Image as ImageIcon,
  Lock,
  Maximize2,
  RefreshCw,
  Ruler,
  Share2,
  ShoppingBag,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { authStore } from "../../lib/authStore";
import { useTryonJobs } from "../../lib/tryonJobs";
import { analytics } from "../../lib/analytics";
import { useCart } from "../../lib/cart";
import { formatAZN, pickLocale } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { TryOnIcon } from "../storefront/TryOnBadge";
import { AtelierModal } from "./AtelierModal";
import { modalSurfaceEffect } from "../../lib/modalSurface";
import { PillCTA, SmallCaps } from "./primitives";

const GUEST_TOKEN_KEY = "atelier_tryon_guest_token";
const LOADER_STAGES = [
  "Foto yoxlanılır",
  "Məhsul hazırlanır",
  "Görünüş yaradılır",
];
const SLOT_COPY = {
  upper: { label: "Üst geyim" },
  lower: { label: "Alt geyim" },
  layer: { label: "Üst qat" },
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function imageOf(product) {
  return (
    product?.thumbnail_url
    || safeArray(product?.images).map((image) => image.url).find(Boolean)
    || product?.image
    || ""
  );
}

function titleOf(product, locale = "az") {
  return pickLocale(product?.title, locale) || product?.name || product?.handle || "Atelier piece";
}

function priceOf(product, variant) {
  return variant?.price ?? product?.min_price ?? product?.price ?? null;
}

function productMeta(product, locale = "az") {
  return [
    product?.color,
    product?.material,
    product?.fit_type,
    safeArray(product?.occasion_tags)[0],
  ].filter(Boolean).slice(0, 2).join(" · ") || (locale === "az" ? "Atelier parça" : "Atelier piece");
}

/** Canonical Try-On slot → the modal's three visual columns. */
const SLOT_TO_COLUMN = { TOP: "upper", BOTTOM: "lower", OUTERWEAR: "layer", FULL_BODY: "layer" };
const NON_RENDERABLE_LOOK_SLOTS = new Set(["shoe", "shoes", "footwear", "belt", "tie", "accessory", "accessories"]);
function slotForProduct(product) {
  // The backend owns this now. Guessing a garment type from a product name is
  // how a jacket ends up in the shirt slot; the regex below survives only for
  // rows the backfill could not classify, and never overrides a real value.
  const curatedSlot = String(product?.curatedSlot || product?.slot || "").toLowerCase();
  if (NON_RENDERABLE_LOOK_SLOTS.has(curatedSlot)) return null;
  const canonical = SLOT_TO_COLUMN[product?.try_on_slot];
  if (canonical) return canonical;
  const text = [
    product?.product_type,
    product?.category_id,
    product?.handle,
    titleOf(product, "en"),
    titleOf(product, "az"),
    ...(product?.occasion_tags || []),
  ].join(" ").toLowerCase();
  if (/(shoe|sneaker|boot|loafer|belt|tie|qalstuk|accessor|aksesuar|cufflink|sock)/.test(text)) return null;
  if (/(trouser|pant|jean|short|lower|bottom)/.test(text)) return "lower";
  if (/(jacket|coat|blazer|suit|tuxedo|outer|layer|overcoat|lapel)/.test(text)) return "layer";
  return "upper";
}

function normalizePiece(product, locale, selectedVariant = null, locked = false) {
  if (!product) return null;
  const variants = safeArray(product.variants);
  const variant = selectedVariant || variants.find((item) => Number(item.stock || 0) > 0) || variants[0] || null;
  return {
    ...product,
    locked,
    selectedVariant: variant,
    displayTitle: titleOf(product, locale),
    displayMeta: productMeta(product, locale),
    displayImage: imageOf(product),
    displayPrice: priceOf(product, variant),
  };
}

function slotLabel(slot) {
  return SLOT_COPY[slot]?.label || "Parça";
}

function isReusableSavedAvatar(avatar) {
  const source = avatar?.source || avatar?.params?.mode || avatar?.provider;
  return Boolean(avatar?.id && avatar?.image_url && ["uploaded_photo", "ai_generated"].includes(source));
}

export function TryOnModal({
  open,
  onClose,
  productId,
  productHandle,
  productTitle,
  product = null,
  selectedVariant = null,
  currentFit = "classic",
  variantId,
  runtimeConfig = null,
  mode = "modal",
  colourValueId = null,
}) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const tryonJobs = useTryonJobs();
  // Nəticə dock-u: UI görünəndə gizlənsin; tamamlanma anında UI açıq idisə
  // iş silinir (müştəri nəticəni onsuz da görür), bağlı idisə dock göstərir.
  const mountedRef = useRef(true);
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; tryonJobs?.setUiOpen(Boolean(open)); }, [open, tryonJobs]);
  useEffect(() => () => { mountedRef.current = false; tryonJobs?.setUiOpen(false); }, [tryonJobs]);
  const { locale, t } = useI18n();
  const { addItem, addLook } = useCart() || {};
  const selectedProduct = useMemo(() => ({
    ...(product || {}),
    id: product?.id || productId,
    handle: product?.handle || productHandle,
    title: product?.title || { az: productTitle, en: productTitle },
  }), [product, productId, productHandle, productTitle]);
  const selectedTitle = productTitle || titleOf(selectedProduct, locale);
  const anchorSlot = useMemo(() => slotForProduct(selectedProduct), [selectedProduct]);
  const [quota, setQuota] = useState(null);
  const [stage, setStage] = useState("upload");
  const [lookPieces, setLookPieces] = useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [photoCheck, setPhotoCheck] = useState(null);
  const [photoSource, setPhotoSource] = useState("upload");
  const [savedAvatar, setSavedAvatar] = useState(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaderIndex, setLoaderIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!open) return;
    const lockedPiece = normalizePiece(selectedProduct, locale, selectedVariant, true);
    setLookPieces({ [anchorSlot]: lockedPiece });
    setStage("upload");
    setFile(null);
    setPreview(null);
    setPhotoCheck(null);
    setPhotoSource("upload");
    setSavedAvatar(null);
    setConsent(false);
    setResult(null);
    setError("");
    setNotice("");
  }, [open, selectedProduct, selectedVariant, anchorSlot, locale]);

  useEffect(() => {
    if (!open) return;
    const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
    const headers = guestToken && !authStore.getAccessToken() ? { "X-Tryon-Guest-Token": guestToken } : {};
    api.get("/tryon/quota", { headers })
      .then((response) => setQuota(response.data))
      .catch(() => setQuota(runtimeConfig || { enabled: false, provider_ready: false }));
  }, [open, runtimeConfig]);

  useEffect(() => {
    if (!open || !authStore.getAccessToken()) return;
    setAvatarLoadFailed(false);
    api.get("/me/avatar")
      .then((response) => {
        setSavedAvatar(response.data);
        if (isReusableSavedAvatar(response.data)) setPhotoSource("saved");
      })
      .catch(() => {
        // "No avatar yet" is a 200 with a null id, which `isReusableSavedAvatar`
        // already handles — so reaching this catch means the request genuinely
        // failed. Swallowing it turned the button into "Avatar yarat" and sent a
        // customer who already has an avatar off to the account page to make a
        // second one.
        setSavedAvatar(null);
        setAvatarLoadFailed(true);
      });
  }, [open]);

  useEffect(() => {
    if (!submitting) {
      setLoaderIndex(0);
      return undefined;
    }
    const id = setInterval(() => setLoaderIndex((index) => (index + 1) % LOADER_STAGES.length), 2200);
    return () => clearInterval(id);
  }, [submitting]);

  useEffect(() => () => {
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
  }, [preview]);

  // Browser Back closes the drawer instead of leaving the page. Opening pushes
  // a history entry; Back pops it and we close; closing by button/Esc consumes
  // the entry so a later Back is not a no-op.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const historyPushed = useRef(false);
  useEffect(() => {
    // Yalnız MODAL üçün: səhifə rejimində bu oyun tərs işləyirdi — "Bağla"
    // məhsula keçirir, cleanup-dakı history.back() isə adamı yenidən try-on-a
    // qaytarırdı. Səhifədə Back onsuz da təbii yolla PDP-yə aparır.
    if (!open || mode === "page") return undefined;
    window.history.pushState({ adfDrawer: "tryon" }, "");
    historyPushed.current = true;
    const onPop = () => { historyPushed.current = false; onCloseRef.current?.(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (historyPushed.current) {
        historyPushed.current = false;
        window.history.back();
      }
    };
  }, [open, mode]);

  const availability = quota || runtimeConfig;
  const isUnavailable = Boolean(availability && (!availability.enabled || !availability.provider_ready));
  const uploadLimit = availability?.max_upload_mb || 15;
  const retentionHours = availability?.photo_ttl_hours || 24;
  const selectedPiece = lookPieces[anchorSlot] || normalizePiece(selectedProduct, locale, selectedVariant, true);
  const hasSavedAvatar = isReusableSavedAvatar(savedAvatar);
  const usingSavedAvatar = photoSource === "saved" && hasSavedAvatar;

  const activePieces = useMemo(() => {
    return Object.entries(lookPieces)
      .filter(([, piece]) => piece)
      .map(([slot, piece]) => ({ slot, piece }));
  }, [lookPieces]);

  const total = activePieces.reduce((sum, { piece }) => sum + Number(piece.displayPrice || 0), 0);
  /* Only the subject verdict gates a generation — the same rule the server
     applies. Blocking on the overall status refused photos whose *framing* or
     *lighting* merely drew a note, which is how an ordinary upload ended up
     showing "Fotonu dəyiş" with nothing wrong with it.

     Even the subject check is overridable, because it is demonstrably wrong
     sometimes: cropping one product photograph from portrait to landscape —
     same person, same pixels — flips its verdict from "a person is visible" to
     "we could not find a person". A detector that loses a model it just found
     must not have the last word on whether a customer may try a jacket on. */
  /* Only a file we cannot read stops a generation. Everything else — no person
     found, odd framing, dim light — is advice, shown in full so the customer
     knows what to change if the result disappoints.

     The subject check used to block. It is four Haar cascades and they are
     wrong often enough to matter: recrop one product photograph from portrait
     to landscape and its verdict flips from "a person is visible" to "we could
     not find a person". Shipped as a wall, that told real customers to replace
     a photograph that had worked before. A false refusal costs a sale; a false
     pass costs one generation. */
  const fileUnusable = photoCheck?.status === "error";
  const photoNotes = (photoCheck?.checks || []).filter((c) => c.status !== "pass");
  const photoRejected = fileUnusable;
  // `checking` closes the race: runPhotoCheck clears the previous verdict before
  // it awaits, and a fast click in that window used to walk past the gate.
  const canGenerate = Boolean(
    (usingSavedAvatar || file)
    && consent
    && !submitting
    && !isUnavailable
    && (usingSavedAvatar || (Boolean(photoCheck) && !photoRejected))
  );

  const runPhotoCheck = async (nextFile) => {
    const fd = new FormData();
    fd.append("photo", nextFile);
    setPhotoCheck(null);
    try {
      const response = await api.post("/tryon/photo-check", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoCheck(response.data);
    } catch (requestError) {
      setPhotoCheck({
        status: "error",
        message: requestError?.response?.data?.detail || "Foto yoxlanıla bilmədi.",
        checks: [],
      });
    }
  };

  const onFilePick = (event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(nextFile.type)) {
      setError("JPG, PNG və ya WEBP formatında foto seçin.");
      return;
    }
    if (nextFile.size > uploadLimit * 1024 * 1024) {
      setError(`Foto ${uploadLimit} MB-dan kiçik olmalıdır.`);
      return;
    }
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setFile(nextFile);
    setPreview(URL.createObjectURL(nextFile));
    setPhotoSource("upload");
    setError("");
    runPhotoCheck(nextFile);
  };

  const payloadLookItems = () =>
    Object.entries(lookPieces).reduce((acc, [slot, piece]) => {
      if (!piece) return acc;
      acc[slot] = {
        product_id: piece.id,
        handle: piece.handle,
        variant_id: piece.selectedVariant?.id || null,
      };
      return acc;
    }, {});

  const companionProductIds = () =>
    Object.entries(lookPieces)
      .filter(([slot, piece]) => piece && slot !== anchorSlot && piece.id)
      .map(([, piece]) => piece.id);

  /** Ask the backend whether this look is legal before spending a generation. */
  const validateLook = async (ids) => {
    if (!ids.length) return null;
    try {
      const { data } = await api.post("/tryon/validate-look", {
        product_ids: [selectedProduct.id || productId, ...ids],
      });
      if (data.ok) return null;
      const code = data.errors?.[0]?.code;
      return t(`tryon.error.${code}`, data.errors?.[0]?.message
        || t("tryon.error.TRY_ON_INVALID_COMBINATION", "This combination cannot be rendered."));
    } catch {
      // A validation outage must not block the customer: the generation
      // endpoint applies the same rules and is the authority anyway.
      return null;
    }
  };

  const generate = async () => {
    if (usingSavedAvatar && !authStore.getAccessToken()) {
      setStage("login");
      return;
    }
    if (!usingSavedAvatar && !file) {
      setStage("upload");
      setError("Tam bədən fotosu seçin və ya saxlanmış avatardan istifadə edin.");
      return;
    }
    if (!consent) {
      setStage("upload");
      setError("AI sınaqdan əvvəl məlumat istifadəsinə razılıq verin.");
      return;
    }
    if (!usingSavedAvatar && photoRejected) {
      analytics.tryOn.photoRejected(photoCheck?.status);
      setError(photoCheck.message || "Bu faylı oxuya bilmirik — başqa şəkil seçin.");
      return;
    }
    const combinationError = await validateLook(companionProductIds());
    if (combinationError) {
      setStage("upload");
      setError(combinationError);
      return;
    }
    setSubmitting(true);
    setStage("loading");
    setError("");
    setNotice("");
    const fd = new FormData();
    fd.append("product_id", selectedProduct.id || productId);
    const chosenVariantId = selectedVariant?.id || variantId || selectedPiece?.selectedVariant?.id;
    if (chosenVariantId) fd.append("variant_id", chosenVariantId);
    // Seçilmiş rəngin qarment şəkli işlədilsin — heç vaxt başqa rənginki yox.
    if (colourValueId) fd.append("colour_value_id", colourValueId);
    fd.append("fit", currentFit);
    fd.append("consent", "true");
    // One key per generation attempt. A network retry reuses this FormData and
    // its key, so the backend replays the first result instead of billing a
    // second; pressing "Try again" builds a fresh FormData with a fresh key.
    const idempotencyKey = (window.crypto?.randomUUID?.()
      || `tryon-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    fd.append("idempotency_key", idempotencyKey);
    fd.append("use_saved_avatar", usingSavedAvatar ? "true" : "false");
    if (!usingSavedAvatar) fd.append("photo", file);
    fd.append("look_items", JSON.stringify(payloadLookItems()));
    // The companions are the garments the provider actually renders; look_items
    // only ever reached the prompt as text.
    const companionIds = companionProductIds();
    if (companionIds.length) fd.append("companion_product_ids", companionIds.join(","));
    const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
    const headers = { "Content-Type": "multipart/form-data" };
    if (guestToken && !authStore.getAccessToken()) headers["X-Tryon-Guest-Token"] = guestToken;
    try {
      analytics.tryOn.submit(selectedPiece, usingSavedAvatar ? "saved_avatar" : "upload");
      const request = api.post(usingSavedAvatar ? "/me/tryon" : "/tryon", fd, { headers });
      tryonJobs?.track(
        { title: selectedTitle, handle: selectedProduct?.handle },
        request.then((r) => r.data),
      );
      const response = await request;
      // Nəticə bu ekranda göstərilirsə dock lazım deyil; müştəri çıxıbsa
      // (səhifə tərk edilib və ya modal bağlanıb) iş dock-da qalır.
      if (mountedRef.current && openRef.current) tryonJobs?.clear();
      if (response.data.guest_token) localStorage.setItem(GUEST_TOKEN_KEY, response.data.guest_token);
      setResult(response.data);
      analytics.tryOn.success(selectedPiece, response.data?.provider, response.data?.is_fallback);
      setQuota((current) => ({
        ...(current || {}),
        remaining_today: response.data.remaining_today,
        daily_limit: response.data.daily_limit || current?.daily_limit,
      }));
      setStage("result");
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 429) {
        setStage("limit");
        setError("Bu gün üçün AI sınaq limitiniz bitib.");
      } else {
        setStage("upload");
        const code = requestError?.response?.headers?.["x-tryon-error-code"];
        const localised = code ? t(`tryon.error.${code}`, null) : null;
        setError(localised || requestError?.response?.data?.detail
          || t("tryon.generic_error", "AI sınaq tamamlanmadı."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const saveToWardrobe = async () => {
    if (!result?.result_id) return;
    if (!authStore.getAccessToken()) {
      setStage("login");
      return;
    }
    try {
      await api.post("/me/tryon/save-to-wardrobe", {
        result_id: result.result_id,
        guest_token: localStorage.getItem(GUEST_TOKEN_KEY),
      });
      setResult({ ...result, savedToWardrobe: true });
      setNotice("Qarderoba saxlandı.");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Nəticə qarderoba saxlanmadı.");
    }
  };

  const addLookToBag = async () => {
    const variants = activePieces.reduce((acc, { slot, piece }) => {
      const id = piece?.selectedVariant?.id;
      if (id) acc[slot] = id;
      return acc;
    }, {});
    const ids = Object.values(variants);
    if (!ids.length) {
      // No size was chosen before the fitting — rather than dead-end on an
      // error, send the shopper to the product to pick a size and buy. The
      // result must always lead somewhere purchasable.
      navigate(`/products/${selectedProduct.handle || productHandle}`);
      return;
    }
    try {
      if (Object.keys(variants).length > 1 && addLook) {
        await addLook(selectedProduct.handle || productHandle, variants);
      } else if (addItem) {
        await addItem(ids[0], 1);
      }
      analytics.addToCart(selectedProduct, { quantity: ids.length });
      navigate("/cart");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Səbətə əlavə edilmədi.");
    }
  };

  /**
   * The result as a Blob.
   *
   * `result_url` is a signed path on the API origin (:8000) while the page is
   * served from :3000, so anything that relies on same-origin behaviour fails.
   * CORS already permits the fetch — `main.py` allows the storefront origin.
   */
  const fetchResultBlob = async () => {
    const response = await fetch(mediaUrl(result.result_url), { credentials: "omit" });
    if (!response.ok) throw new Error(`media ${response.status}`);
    return response.blob();
  };

  const downloadResult = async () => {
    if (!result?.result_url) return;
    setNotice("");
    try {
      // The `download` attribute is honoured only for same-origin, blob: and
      // data: URLs. Pointed at the cross-origin signed URL it was silently
      // dropped, `target="_blank"` took over, and the browser opened the image
      // in a tab instead of saving it — the API sends no Content-Disposition
      // either, so nothing else made it a download. Fetching to a blob puts the
      // bytes on this origin, where `download` means what it says.
      const blob = await fetchResultBlob();
      analytics.tryOn.downloaded(selectedPiece);
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = "atelier-ai-look.jpg";
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked on the next tick; Safari needs the URL to outlive the click.
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
    } catch (downloadError) {
      console.error("[atelier] try-on download failed", downloadError);
      setNotice(t("tryon.download_error", "Şəkli yükləmək alınmadı. Yenidən cəhd edin."));
    }
  };

  const shareResult = async () => {
    if (!result?.result_url) return;
    setNotice("");
    // Share the picture, not a link. The link route needed a public page the
    // SPA never had (`/tryon/shared/:slug` → NotFound) and a mint endpoint that
    // requires sign-in, which guests — the default Try-On flow — do not have.
    // A file is also what someone actually wants to put in WhatsApp.
    try {
      const blob = await fetchResultBlob();
      const file = new File([blob], "atelier-ai-look.jpg", { type: blob.type || "image/jpeg" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: t("tryon.share_title", "Atelier AI görünüşünüz"),
        });
        analytics.tryOn.shared(selectedPiece);
        return;
      }
      // No share sheet here — saving the image is the useful fallback.
      await downloadResult();
    } catch (shareError) {
      // Dismissing the share sheet throws AbortError. That is the customer
      // changing their mind, not a failure, and it used to say "alınmadı".
      if (shareError?.name === "AbortError") return;
      console.error("[atelier] try-on share failed", shareError);
      setNotice(t("tryon.share_error", "Paylaşmaq alınmadı."));
    }
  };

  const closeModal = () => {
    onClose?.();
    setTimeout(() => {
      setError("");
      setNotice("");
    }, 300);
  };

  // On the dedicated page the fixed mobile tab bar would sit on top of the
  // result's action buttons, so it is removed while the fitting page is open —
  // the same contract the atelier modals use. Restored on unmount.
  useEffect(() => {
    if (mode !== "page") return undefined;
    return modalSurfaceEffect(true);
  }, [mode]);

  const body = (
      <section className="min-w-0">
          {stage === "upload" && (
            <UploadScreen
              fileRef={fileRef}
              preview={preview}
              file={file}
              photoCheck={photoCheck}
              photoSource={photoSource}
              setPhotoSource={setPhotoSource}
              savedAvatar={savedAvatar}
              hasSavedAvatar={hasSavedAvatar}
              avatarLoadFailed={avatarLoadFailed}
              consent={consent}
              setConsent={setConsent}
              onFilePick={onFilePick}
              uploadLimit={uploadLimit}
              retentionHours={retentionHours}
              error={error}
              onManageAvatar={() => navigate("/account?tab=avatar")}
              onGenerateLook={() => generate(false)}
              canGenerate={canGenerate}
              activePieces={activePieces}
              selectedPiece={selectedPiece}
              selectedTitle={selectedTitle}
              isUnavailable={isUnavailable}
              locale={locale}
            />
          )}

          {stage === "loading" && (
            <LoadingScreen
              stage={LOADER_STAGES[loaderIndex]}
              selectedTitle={selectedTitle}
              onContinue={closeModal}
            />
          )}

          {stage === "result" && (
            <ResultScreen
              result={result}
              activePieces={activePieces}
              total={total}
              locale={locale}
              notice={notice}
              error={error}
              onAddToBag={addLookToBag}
              onSave={saveToWardrobe}
              onChange={() => setStage("upload")}
              onTryAgain={() => {
                setResult(null);
                setStage("upload");
              }}
              onDownload={downloadResult}
              onShare={shareResult}
              onZoom={() => window.open(mediaUrl(result?.result_url), "_blank", "noopener,noreferrer")}
              onBook={() => navigate("/contact")}
            />
          )}

          {stage === "login" && (
            <LoginScreen
              productHandle={selectedProduct.handle || productHandle}
              onBack={() => setStage(result ? "result" : "upload")}
            />
          )}

          {stage === "limit" && (
            <LimitScreen
              error={error}
              onBack={() => setStage("upload")}
              onStyle={() => navigate("/contact")}
              isGuest={!authStore.getAccessToken()}
              onRegister={() => navigate(`/auth/register?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            />
          )}
      </section>
  );

  // A dedicated, minimal full-page presentation — the process lives on its own
  // route instead of a modal over the product. Same stages, same handlers.
  if (mode === "page") {
    return (
      <div className="adf-tryon-page" data-testid="tryon-page">
        <div className="adf-tryon-page__head adf-container">
          <span className="adf-tryon-page__eyebrow">{t("tryon.modal_title", "AI geyinmə otağı")}</span>
          <button
            type="button"
            onClick={closeModal}
            className="adf-tryon-page__close"
            aria-label={t("common.close", "Bağla")}
            data-testid="tryon-page-close"
          >
            <X size={16} strokeWidth={1.6} /> {t("common.close", "Bağla")}
          </button>
        </div>
        <div className="adf-tryon-page__body adf-container">{body}</div>
      </div>
    );
  }

  return (
    <AtelierModal
      open={open}
      onClose={closeModal}
      title={t("tryon.modal_title", "AI geyinmə otağı")}
      subtitle={t("tryon.before_fitting", "Məhsulu üzərinizdə sadə şəkildə yoxlayın.")}
      testid="tryon-modal"
      width="min(1040px, 100vw)"
    >
      {body}
    </AtelierModal>
  );
}

function UploadScreen({
  fileRef,
  preview,
  file,
  photoCheck,
  photoSource,
  setPhotoSource,
  savedAvatar,
  hasSavedAvatar,
  avatarLoadFailed,
  consent,
  setConsent,
  onFilePick,
  uploadLimit,
  retentionHours,
  error,
  onManageAvatar,
  onGenerateLook,
  canGenerate,
  activePieces,
  selectedPiece,
  selectedTitle,
  isUnavailable,
  locale,
}) {
  const { t } = useI18n();
  const usingSavedAvatar = photoSource === "saved" && hasSavedAvatar;
  const savedAvatarSource = savedAvatar?.source || savedAvatar?.params?.mode || savedAvatar?.provider;
  const savedAvatarLabel = savedAvatarSource === "ai_generated" ? "AI avatar" : "Real avatar";
  const activePreview = usingSavedAvatar ? mediaUrl(savedAvatar?.image_url) : preview;
  // Same rule as the parent: only a file we cannot read stops a generation.
  const fileUnusable = photoCheck?.status === "error";
  const photoNotes = (photoCheck?.checks || []).filter((c) => c.status !== "pass");
  const photoRejected = fileUnusable;
  const photoStatus = usingSavedAvatar
    ? "Saxlanmış avatar istifadə olunacaq."
    : fileUnusable
      ? photoCheck.message
      : file
        ? (photoCheck
          ? (photoNotes.length ? "Foto qəbul edildi — aşağıdakı qeydlərə baxın." : "Foto hazırdır.")
          : "Foto yoxlanılır…")
        : "Foto və ya avatar seçin.";
  const consentTone = consent
    ? "border-graphite/10 bg-white text-graphite/75"
    : "border-espresso/45 bg-stone/35 text-graphite";

  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_280px] lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="space-y-4">
        <div className="flex gap-3 border-b border-graphite/10 pb-4">
          <div className="h-20 w-16 flex-none overflow-hidden bg-stone/35 sm:h-24 sm:w-20">
            {selectedPiece?.displayImage ? (
              <img loading="lazy" decoding="async" src={mediaUrl(selectedPiece.displayImage)} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-graphite/45">
                <ImageIcon size={22} strokeWidth={1.2} />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <SmallCaps>{t("tryon.selected_piece", "Seçilmiş parça")}</SmallCaps>
            <h2 className="mt-1 font-serif text-[clamp(24px,4vw,36px)] leading-none text-graphite">{selectedTitle}</h2>
            <p className="mt-1 text-sm leading-5 text-graphite/70">
              {selectedPiece?.displayMeta}
              {selectedPiece?.selectedVariant?.title ? ` · ${selectedPiece.selectedVariant.title}` : ""}
              {selectedPiece?.displayPrice ? ` · ${formatAZN(selectedPiece.displayPrice, locale)}` : ""}
            </p>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-[24px] leading-[1.02] text-graphite md:text-[clamp(30px,3.4vw,44px)] md:leading-[0.98]">
            {usingSavedAvatar ? `${savedAvatarLabel} ilə sına.` : "Üzərinizdə görün."}
          </h3>
          <p className="mt-2 hidden max-w-xl text-sm leading-6 text-graphite/72 md:block">
            Saxlanmış avatarı seçin və ya bir tam bədən fotosu yükləyin. Sonra AI nəticəsini yaradın.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              if (hasSavedAvatar) {
                setPhotoSource("saved");
              } else {
                onManageAvatar();
              }
            }}
            className={`flex min-h-[46px] items-center justify-center gap-2 border px-3 py-2 text-[10px] uppercase tracking-[0.1em] transition sm:text-[11px] ${
              usingSavedAvatar ? "border-graphite bg-graphite text-ivory" : "border-graphite/15 bg-white text-graphite hover:border-espresso"
            }`}
            data-testid="tryon-use-saved-avatar"
          >
            <UserRound size={16} strokeWidth={1.5} />
            {hasSavedAvatar ? savedAvatarLabel : "Avatar yarat"}
          </button>
          {/* Bir düymə, hər cihazda. Telefondakı Kamera/Qalereya cütü eyni OS
              pəncərəsini açırdı — iOS `capture`-ı çox vaxt saya salmır və
              seçimi onsuz da özü təklif edir. */}
          <button
            type="button"
            onClick={() => {
              setPhotoSource("upload");
              fileRef.current?.click();
            }}
            className={`flex min-h-[46px] items-center justify-center gap-2 border px-3 py-2 text-[10px] uppercase tracking-[0.1em] transition sm:text-[11px] ${
              !usingSavedAvatar ? "border-graphite bg-graphite text-ivory" : "border-graphite/15 bg-white text-graphite hover:border-espresso"
            }`}
            data-testid="tryon-source-upload"
          >
            <Upload size={16} strokeWidth={1.5} /> Şəkil yüklə
          </button>
        </div>

        {!usingSavedAvatar && fileUnusable && (
          <div role="alert" data-testid="tryon-photo-rejected" className="border border-espresso/30 bg-white p-3">
            <p className="m-0 text-[13px] leading-5 text-graphite">{photoCheck.message}</p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="tryon-change-photo"
              className="mt-3 min-h-11 text-[11px] uppercase tracking-[0.1em] text-espresso underline underline-offset-4"
            >
              Başqa foto seç
            </button>
          </div>
        )}

        {/* Not a refusal — these say what may spoil the result, item by item, so
            the customer can decide for themselves whether to swap the photo.
            "Change your photo" with no reason was the whole complaint. */}
        {!usingSavedAvatar && !fileUnusable && photoNotes.length > 0 && (
          <div data-testid="tryon-photo-notes" className="border border-graphite/15 bg-white p-3">
            <p className="m-0 text-[12px] uppercase tracking-[0.1em] text-graphite/60">Fotoya dair qeydlər</p>
            <ul className="mt-2 space-y-1">
              {photoNotes.map((c) => (
                <li key={c.key} className="text-[13px] leading-5 text-graphite">
                  <span className="text-graphite/55">{c.label}: </span>{c.message}
                </li>
              ))}
            </ul>
            <p className="mt-2 mb-0 text-[12px] leading-5 text-graphite/60">
              Yenə də davam edə bilərsiniz — bu qeydlər nəticənin keyfiyyətinə aiddir.
            </p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="tryon-change-photo"
              className="mt-3 min-h-11 text-[11px] uppercase tracking-[0.1em] text-espresso underline underline-offset-4"
            >
              Başqa foto seç
            </button>
          </div>
        )}

        {avatarLoadFailed && (
          <p role="alert" className="text-[13px] leading-5 text-burgundy" data-testid="tryon-avatar-load-error">
            Saxlanmış avatarınızı yükləmək alınmadı. Yenidən cəhd edin və ya foto yükləyin.
          </p>
        )}

        <div
          role="button"
          tabIndex={0}
          onClick={() => { if (!usingSavedAvatar) fileRef.current?.click(); }}
          onKeyDown={(event) => { if (event.key === "Enter" && !usingSavedAvatar) fileRef.current?.click(); }}
          className={`relative min-h-[148px] overflow-hidden border bg-paper sm:min-h-[280px] md:min-h-[340px] ${
            usingSavedAvatar ? "border-graphite/12" : "cursor-pointer border-dashed border-graphite/28 hover:border-espresso"
          }`}
          data-testid="tryon-upload-zone"
        >
          {activePreview ? (
            <>
              <img loading="lazy" decoding="async" src={activePreview} alt="" className="absolute inset-0 h-full w-full object-contain p-5" />
              {!usingSavedAvatar && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    fileRef.current?.click();
                  }}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 border border-graphite bg-ivory px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-graphite transition hover:bg-graphite hover:text-ivory"
                >
                  Fotonu dəyiş
                </button>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-5 text-center">
              <Upload size={20} strokeWidth={1.2} className="text-espresso" />
              <span className="mt-2 block font-serif text-[18px] leading-tight text-graphite sm:text-[26px]">Foto yükləyin</span>
              <span className="mt-1.5 max-w-sm text-[13px] leading-5 text-graphite/70">
                Tam bədən, üz aydın, fon sadə; qollar bədəni örtməsin.
              </span>
              <span className="mt-2 text-[10px] uppercase tracking-[0.12em] text-graphite/58">JPG · PNG · WEBP · max {uploadLimit} MB</span>
            </div>
          )}
          {/* Tək input, `capture`-sız: OS öz pəncərəsində kamera ilə qalereyanı
              birlikdə təklif edir. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFilePick}
            data-testid="tryon-file-input"
          />
        </div>

        <div className="space-y-3 md:hidden">
          <ConsentNotice
            consent={consent}
            setConsent={setConsent}
            usingSavedAvatar={usingSavedAvatar}
            retentionHours={retentionHours}
            tone={consentTone}
          />
          {error && <div className="border border-red-900/15 bg-red-50 p-3 text-sm text-red-900" data-testid="tryon-error">{error}</div>}
          <PillCTA
            variant="primary"
            onClick={onGenerateLook}
            disabled={!canGenerate}
            className={`w-full ${!canGenerate ? "opacity-60" : ""}`}
            data-testid="tryon-submit"
          >
            {t("tryon.try_this_look", "AI ilə sına")}
          </PillCTA>
        </div>
      </div>

      <aside className="hidden space-y-4 md:block md:sticky md:top-6 md:self-start">
        <div className="border-y border-graphite/12 py-4">
          <SmallCaps>{t("tryon.fitting_summary", "Sınaq")}</SmallCaps>
          <div className="mt-3 space-y-2 text-sm leading-6 text-graphite/72">
            <div className="flex justify-between gap-4">
              <span>Parça</span>
              <span className="text-graphite">{activePieces.length}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Mənbə</span>
              <span className="text-right text-graphite">{usingSavedAvatar ? savedAvatarLabel : (file?.name || "Seçilməyib")}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Status</span>
              <span className={`text-right ${photoRejected ? "text-red-900" : "text-graphite"}`}>{isUnavailable ? "AI aktiv deyil" : photoStatus}</span>
            </div>
          </div>
        </div>

        <ConsentNotice
          consent={consent}
          setConsent={setConsent}
          usingSavedAvatar={usingSavedAvatar}
          retentionHours={retentionHours}
          tone={consentTone}
        />

        {error && <div className="border border-red-900/15 bg-red-50 p-3 text-sm text-red-900" data-testid="tryon-error">{error}</div>}

        <PillCTA
          variant="primary"
          onClick={onGenerateLook}
          disabled={!canGenerate}
          className={`w-full ${!canGenerate ? "opacity-60" : ""}`}
          data-testid="tryon-submit"
        >
          {t("tryon.try_this_look", "AI ilə sına")}
        </PillCTA>

        <p className="text-xs leading-5 text-graphite/58">
          Nəticə vizual önizləmədir. Final ölçü üçün ölçü cədvəli və stilist məsləhəti əsas götürülməlidir.
        </p>
      </aside>
    </div>
  );
}

function ConsentNotice({ consent, setConsent, usingSavedAvatar, retentionHours, tone }) {
  return (
    <label className={`flex gap-3 border p-4 text-sm leading-6 ${tone}`} data-testid="tryon-consent-row">
      <input
        type="checkbox"
        checked={consent}
        onChange={(event) => setConsent(event.target.checked)}
        className="mt-1 accent-espresso"
        data-testid="tryon-consent"
      />
      <span>
        <span className="block font-medium text-graphite">
          {consent ? "Təsdiqləndi" : "Davam etmək üçün təsdiqləyin"}
        </span>
        <span className="mt-1 block">
          {usingSavedAvatar
            ? "Saxlanmış avatarım bu AI sınaqda istifadə oluna bilər və mən silənə qədər hesabımda qalır."
            : `Fotom yalnız bu AI sınaq üçün istifadə olunur və ${retentionHours} saat ərzində avtomatik silinir.`}
        </span>
      </span>
    </label>
  );
}

function LoadingScreen({ stage, selectedTitle, onContinue }) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-[560px] flex-col justify-between overflow-hidden bg-graphite p-8 text-ivory">
      <div className="flex items-center justify-between">
        <SmallCaps>{t("tryon.loader.title", "AI sınaq")}</SmallCaps>
        <TryOnIcon className="h-7 w-7 text-espresso" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.26em] text-ivory/58">{selectedTitle}</p>
        <h2 className="mt-5 font-serif text-[clamp(44px,7vw,88px)] leading-none">{stage}</h2>
        <div className="mt-8 grid gap-3">
          {LOADER_STAGES.map((item) => (
            <div key={item} className={`h-px ${item === stage ? "bg-espresso" : "bg-ivory/12"}`} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="max-w-md text-sm leading-6 text-ivory/68">
          Nəticə hazırlanır. Səhifəyə qayıda bilərsiniz — hazır olanda ekranın aşağısında kiçik pəncərədə görünəcək, toxunanda böyüyəcək.
        </p>
        <PillCTA variant="secondary" onClick={onContinue} className="border-ivory/45 text-ivory hover:bg-ivory hover:text-graphite">
          Bağla
        </PillCTA>
      </div>
    </div>
  );
}

function ResultScreen({
  result,
  activePieces,
  total,
  locale,
  notice,
  error,
  onAddToBag,
  onSave,
  onChange,
  onTryAgain,
  onDownload,
  onShare,
  onZoom,
  onBook,
}) {
  const { t } = useI18n();
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <SmallCaps>{t("tryon.your_look", "AI nəticəsi")}</SmallCaps>
            <h2 className="mt-3 font-serif text-[52px] leading-none text-graphite">{t("tryon.your_look", "Üzərinizdə görünüş")}</h2>
          </div>
          <button type="button" onClick={onZoom} className="hidden items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-graphite/70 md:flex">
            <Maximize2 size={14} /> Böyüt
          </button>
        </div>
        {/* The overlay fallback pastes the garment into a corner instead of
            rendering it worn. It used to be indistinguishable from a real
            result, so a customer spent one of their few daily slots on a
            placeholder and had no way to know. */}
        {result?.is_fallback && (
          <p role="status" className="mt-4 border border-espresso/25 bg-paper px-4 py-3 text-[13px] leading-5 text-graphite/80" data-testid="tryon-fallback-notice">
            Bu, təxmini bir görüntüdür — AI render-i hazırda əlçatan deyil. Bir azdan yenidən sınayın.
          </p>
        )}
        <div className="mt-6 overflow-hidden border border-espresso/30 bg-stone/20 p-3">
          <img loading="lazy" decoding="async" src={mediaUrl(result?.result_url)} alt="" className="max-h-[760px] w-full object-contain" data-testid="tryon-result-image" />
        </div>
      </div>

      <aside className="space-y-4">
        <div className="border border-graphite/10 bg-white p-4">
          <SmallCaps>{t("tryon.pieces_in_look", "Sınaqdakı parçalar")}</SmallCaps>
          <div className="mt-4 space-y-3">
            {activePieces.map(({ slot, piece }) => (
              <div key={slot} className="grid grid-cols-[64px_1fr] gap-3">
                <div className="h-20 overflow-hidden bg-stone/35">
                  {piece.displayImage ? <img loading="lazy" decoding="async" src={mediaUrl(piece.displayImage)} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-espresso">{slotLabel(slot)}</div>
                  <div className="truncate font-serif text-[18px]">{piece.displayTitle}</div>
                  <div className="text-xs text-graphite/70">{piece.selectedVariant?.title || "Ölçü sonra"} · {formatAZN(piece.displayPrice, locale)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-graphite/10 pt-4 font-serif text-[24px]">
            <span>{t("tryon.total", "Cəmi")}</span>
            <span>{formatAZN(total, locale)}</span>
          </div>
        </div>

        <div className="border border-graphite/10 bg-stone/20 p-4">
          <div className="flex items-center gap-2">
            <Ruler size={16} strokeWidth={1.4} className="text-espresso" />
            <SmallCaps>{t("tryon.recommended_size", "Qeyd")}</SmallCaps>
          </div>
          <p className="mt-3 text-sm leading-6 text-graphite/70">
            AI nəticəsi vizual önizləmədir. Final ölçü üçün ölçü cədvəli və stilist məsləhəti əsas götürülməlidir.
          </p>
        </div>

        {notice && <div className="border border-emerald-900/15 bg-emerald-50 p-3 text-sm text-emerald-950">{notice}</div>}
        {error && <div className="border border-red-900/15 bg-red-50 p-3 text-sm text-red-900">{error}</div>}

        <div className="grid gap-3">
          <PillCTA variant="primary" onClick={onAddToBag}><ShoppingBag size={15} /> Səbətə əlavə et</PillCTA>
          <PillCTA variant="secondary" onClick={onSave}>{t("tryon.save_wardrobe", "Qarderoba saxla")}</PillCTA>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={onChange} className="border border-graphite/20 px-4 py-3 text-[11px] uppercase tracking-[0.16em]">
              Dəyiş
            </button>
            <button type="button" onClick={onTryAgain} className="border border-graphite/20 px-4 py-3 text-[11px] uppercase tracking-[0.16em]">
              <RefreshCw size={13} className="mr-1 inline" /> Yenidən sına
            </button>
            <button type="button" onClick={onDownload} className="border border-graphite/20 px-4 py-3 text-[11px] uppercase tracking-[0.16em]">
              <Download size={13} className="mr-1 inline" /> Yüklə
            </button>
            <button type="button" onClick={onShare} className="border border-graphite/20 px-4 py-3 text-[11px] uppercase tracking-[0.16em]">
              <Share2 size={13} className="mr-1 inline" /> Paylaş
            </button>
          </div>
          <PillCTA variant="secondary" onClick={onBook}>{t("tryon.book_private", "Stilist görüşü al")}</PillCTA>
        </div>
      </aside>
    </div>
  );
}

function LoginScreen({ productHandle, onBack }) {
  const { t } = useI18n();
  return (
    <div className="mx-auto flex min-h-[520px] max-w-xl flex-col items-center justify-center text-center">
      <Lock size={30} strokeWidth={1.2} className="text-espresso" />
      <h2 className="mt-6 font-serif text-[48px] leading-none text-graphite">{t("tryon.save_fitting", "Save this fitting")}</h2>
      <p className="mt-5 text-sm leading-6 text-graphite/70">
        You can generate a guest preview without signing in. Saving to wardrobe and previous looks requires an Atelier account.
      </p>
      <div className="mt-8 grid w-full gap-3 sm:grid-cols-2">
        <Link to={`/auth/login?next=/products/${productHandle}`} className="adf-cta adf-cta-dark">{t("tryon.sign_in", "Sign in")}</Link>
            <button type="button" onClick={onBack} className="adf-cta adf-cta-light">{t("tryon.continue_guest", "Qonaq kimi davam et")}</button>
      </div>
    </div>
  );
}

function LimitScreen({ error, onBack, onStyle, isGuest = false, onRegister }) {
  const { t } = useI18n();
  return (
    <div className="mx-auto flex min-h-[520px] max-w-xl flex-col items-center justify-center text-center">
      <AlertCircle size={32} strokeWidth={1.2} className="text-espresso" />
      <h2 className="mt-6 font-serif text-[48px] leading-none text-graphite">{t("tryon.fitting_complete", "Today's fitting is complete.")}</h2>
      <p className="mt-5 text-sm leading-6 text-graphite/70">
        {error || "Bu gün üçün AI önizləmə limitiniz istifadə olunub. Stilist xidməti ilə davam edə bilərsiniz."}
      </p>
      {/* Qonağın limiti kiçikdir — sahibin istəyi (2026-09-16): limitdə
          qeydiyyata dəvət və birbaşa keçid göstərilsin. */}
      {isGuest && onRegister && (
        <p className="mt-3 text-sm leading-6 text-graphite/70">
          {t("tryon.limit_register_before", "Yenə AI ilə yoxlamaq istəyirsinizsə, ")}
          <button
            type="button"
            onClick={onRegister}
            className="font-medium text-espresso underline underline-offset-4"
          >
            {t("tryon.limit_register_link", "qeydiyyatdan keçin")}
          </button>
          {t("tryon.limit_register_after", " — qeydiyyatlı istifadəçilərin gündəlik limiti daha yüksəkdir.")}
        </p>
      )}
      <div className="mt-8 grid w-full gap-3 sm:grid-cols-2">
        <button type="button" onClick={onBack} className="adf-cta adf-cta-light">{t("tryon.back_to_room", "Geri qayıt")}</button>
        <button type="button" onClick={onStyle} className="adf-cta adf-cta-dark">{t("tryon.book_styling", "Stilist görüşü al")}</button>
      </div>
    </div>
  );
}
